"use client";

import {
  AnimatedToastStack,
  useAnimatedToastStack,
  type ToastInput,
  type ToastStatus,
} from "@/components/motion/animated-toast-stack";
import { useEffect, useRef, type ReactNode } from "react";

type ToastOptions = {
  id?: string;
  description?: ReactNode;
  duration?: number;
};

type Emit = (input: ToastInput) => string;
type Dismiss = (id: string) => void;
type Update = (id: string, patch: Partial<ToastInput>) => void;

let _emit: Emit | null = null;
let _dismiss: Dismiss | null = null;
let _update: Update | null = null;
const _queued: ToastInput[] = [];

function emit(input: ToastInput): string {
  if (_emit) return _emit(input);
  const id =
    input.id ?? `queued-${_queued.length}-${Date.now().toString(36)}`;
  _queued.push({ ...input, id });
  return id;
}

function makeToast(
  status: ToastStatus,
  title: ReactNode,
  opts?: ToastOptions,
): string {
  return emit({
    id: opts?.id,
    title,
    description: opts?.description,
    duration: opts?.duration,
    status,
  });
}

interface ToastFn {
  (title: ReactNode, opts?: ToastOptions): string;
  success: (title: ReactNode, opts?: ToastOptions) => string;
  error: (title: ReactNode, opts?: ToastOptions) => string;
  info: (title: ReactNode, opts?: ToastOptions) => string;
  warning: (title: ReactNode, opts?: ToastOptions) => string;
  loading: (title: ReactNode, opts?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const toastFn = ((title: ReactNode, opts?: ToastOptions) =>
  makeToast("neutral", title, opts)) as ToastFn;

toastFn.success = (title, opts) => makeToast("success", title, opts);
toastFn.error = (title, opts) => makeToast("error", title, opts);
toastFn.info = (title, opts) => makeToast("info", title, opts);
toastFn.warning = (title, opts) => makeToast("info", title, opts);
toastFn.loading = (title, opts) =>
  makeToast("loading", title, { ...opts, duration: opts?.duration ?? 0 });
toastFn.dismiss = (id) => {
  _dismiss?.(id);
};

export const toast = toastFn;

export function Toaster() {
  const { toasts, showToast, dismissToast, updateToast } = useAnimatedToastStack(
    { defaultDuration: 4200 },
  );

  const showRef = useRef(showToast);
  const dismissRef = useRef(dismissToast);
  const updateRef = useRef(updateToast);
  showRef.current = showToast;
  dismissRef.current = dismissToast;
  updateRef.current = updateToast;

  useEffect(() => {
    _emit = (input) => showRef.current(input);
    _dismiss = (id) => dismissRef.current(id);
    _update = (id, patch) => updateRef.current(id, patch);
    if (_queued.length > 0) {
      const queued = _queued.splice(0);
      queued.forEach((q) => showRef.current(q));
    }
    return () => {
      _emit = null;
      _dismiss = null;
      _update = null;
    };
  }, []);

  return (
    <AnimatedToastStack
      toasts={toasts}
      onDismiss={dismissToast}
      position="bottom-right"
    />
  );
}
